// Immunity to external DOM mutation.
//
// "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
// child of this node" (and its insertBefore twin) is the textbook signature of
// something OUTSIDE React rewriting nodes React owns — Google Translate wraps
// text in <font>, Grammarly / password managers inject and reparent nodes.
// React's reconciler then tries to remove/insert against a node that has been
// moved, the DOM throws NotFoundError, and the whole subtree crashes to an
// ErrorBoundary. None of the app's own direct DOM work (compose contentEditable,
// reading-pane sanitiser) touches a React-managed subtree, so the app can't
// stop this at the source — but it can refuse to crash on it.
//
// Fix: make removeChild / insertBefore tolerant. If the referenced node isn't
// actually a child of `this`, do nothing (React reconciles fine afterwards)
// and report ONCE, as an informational 'dom.mutation' event rather than a
// crash. This is the standard mitigation used against Translate in production
// React apps.
import { reportError } from './reportError';

// Matches the DOM NotFoundError React surfaces for a reparented node.
export function isDomMutationRace(err) {
  const msg = String(err?.message || err || '');
  return /removeChild|insertBefore/.test(msg)
      && /not a child of this node|NotFoundError/i.test(msg);
}

let installed = false;

export function installDomResilience() {
  if (installed) return;
  if (typeof Node === 'undefined' || !Node.prototype) return;
  installed = true;

  const origRemoveChild  = Node.prototype.removeChild;
  const origInsertBefore = Node.prototype.insertBefore;

  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) {
      report('removeChild', child);
      return child;   // React only needs a return value; the node is already gone from here
    }
    return origRemoveChild.call(this, child);
  };

  Node.prototype.insertBefore = function (newNode, refNode) {
    if (refNode && refNode.parentNode !== this) {
      report('insertBefore', refNode);
      // The reference node was moved away — append instead so the new node
      // still lands in this parent rather than throwing.
      return origInsertBefore.call(this, newNode, null);
    }
    return origInsertBefore.call(this, newNode, refNode);
  };
}

function report(op, node) {
  try {
    const tag = node && node.nodeName ? String(node.nodeName).toLowerCase() : '?';
    reportError({
      source:  'dom.mutation',
      message: `External DOM mutation absorbed: ${op} on a reparented <${tag}> (browser extension / translate) — recovered, no crash`,
    });
  } catch { /* absorbing must never itself throw */ }
}
